import { useMemo } from 'react';

import { useLoggedInUserPermissions } from '~/components/Auth';
import { QuickLinkDto } from '~/services/Http/client.people.generated';
import { useGetEmployeeQuickLinksTemp } from '~/services/Http/hooks';

export type UseMenuLinksOutput = {
  generalMenuLinks: QuickLinkDto[];
};

const customLocations = ['PEOP_NEW_EMPLOYEE', 'PEOP_NEW_EMPLOYEE_FORMS'];

export enum NavigatableModule {
  PEOPLE = 'PEOPLE',
  REWARDING = 'REWARDING',
  PERFORMANCE = 'PERFORMANCE',
  DEVELOPMENT = 'DEVELOPMENT',
  ANALYTICS = 'ANALYTICS',
  COLLABORATION = 'COLLABORATION',
  ORGANIZATION = 'ORGANIZATION',
  RECRUITING = 'RECRUITING',
  PLANNING = 'PLANNING',
  SF2 = 'SF2',
}

export enum SF2PathUrl {
  SF2_WORKSPACE = 'SF2_WORKSPACE',
  SF2_HOMEPAGE = 'SF2_HOMEPAGE',
}

export const getSF2Path = (sf2Path: string, includePrefix?: boolean) => {
  switch (sf2Path as SF2PathUrl) {
    case SF2PathUrl.SF2_WORKSPACE:
      return `${includePrefix ? '/v2' : ''}/my-workspace`;
    case SF2PathUrl.SF2_HOMEPAGE:
      return `${includePrefix ? '/v2' : ''}/home`;
    default:
      throw new Error(`${sf2Path} is not a supported menu link`);
  }
};

const sortLinks = (length: number) => (a: QuickLinkDto, b: QuickLinkDto) => {
  const aSeq = !a.seq && a.seq !== 0 ? length : a.seq;
  const bSeq = !b.seq && b.seq !== 0 ? length : b.seq;

  return aSeq - bSeq;
};

const isModuleAvailable = (enabledModules: string[], navModule: string) => {
  if (navModule === NavigatableModule.RECRUITING) {
    return (
      enabledModules.includes('RECRUITING_FULL') ||
      enabledModules.includes('RECRUITING_MINI')
    );
  }

  if (navModule === NavigatableModule.ORGANIZATION) {
    return enabledModules.includes('ORGANISATION');
  }

  if (navModule === NavigatableModule.SF2) {
    return enabledModules.includes('UI2');
  }

  return enabledModules.includes(navModule);
};

const isTabAvailable = (navTab: string, enabledTabs: string[] | undefined) => {
  if (!enabledTabs || customLocations.includes(navTab)) {
    return true; // always allow custom tabs
  }

  return enabledTabs.includes(navTab);
};

const isMenuLinkAvailable =
  (enabledModules: string[], enabledTabs: Record<string, string[]>) =>
  (ql: QuickLinkDto) => {
    if (ql.location !== 'menu_links' || !ql.navigation) {
      return false;
    }

    const { module, tab } = ql.navigation;

    return (
      isModuleAvailable(enabledModules, module) &&
      isTabAvailable(tab, enabledTabs[module])
    );
  };

export const useMenuLinks = () => {
  const { data: allGeneralQuickLinks } = useGetEmployeeQuickLinksTemp({
    full: true,
  });
  const { enabledModules, enabledTabs } = useLoggedInUserPermissions();
  const generalMenuLinks = useMemo(() => {
    const menuLinks = allGeneralQuickLinks
      ? allGeneralQuickLinks.filter(
          isMenuLinkAvailable(enabledModules, enabledTabs),
        )
      : [];

    return menuLinks.slice().sort(sortLinks(menuLinks.length));
  }, [allGeneralQuickLinks, enabledModules, enabledTabs]);

  return {
    generalMenuLinks,
  };
};
